import { useMemo, useState } from "react";
import { IoMdCloseCircle } from "react-icons/io";
import { ROLES } from "../../../Constants/Strings";

const UserAccessModal = ({ folder, usersList, onClose, onSave }) => {
  const [searchText, setSearchText] = useState("");
  const initialSelectedUsers = [...(folder?.allowed_users_info || [])];

  const [selectedUsers, setSelectedUsers] = useState(initialSelectedUsers);

  const [activeTab, setActiveTab] = useState("all"); // "all" | "selected"

  const toggleUser = (userId) => {
    setSelectedUsers((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    );
  };

  const hasAccess = (userId) =>
    selectedUsers.map(String).includes(String(userId));

  // Filter for search
  const filteredAllUsers = usersList.filter((user) =>
    `${user.first_name} ${user.last_name}`
      .toLowerCase()
      .includes(searchText.toLowerCase())
  );

  // Users with access
  const accessUsers = usersList.filter((user) => hasAccess(user.id));

  const isChanged = useMemo(() => {
    if (initialSelectedUsers.length !== selectedUsers.length) return true;
    const sortedA = [...initialSelectedUsers].sort();
    const sortedB = [...selectedUsers].sort();
    return !sortedA.every((id, idx) => id === sortedB[idx]);
  }, [initialSelectedUsers, selectedUsers]);

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black/40 z-50 p-3">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-lg h-[28rem] flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center border-b px-4 py-3">
          <h2 className="text-lg font-semibold">Manage Folder Access</h2>
          <button
            onClick={onClose}
            className="text-gray-600 hover:text-gray-800"
          >
            <IoMdCloseCircle size={24} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b">
          <button
            onClick={() => setActiveTab("all")}
            className={`flex-1 py-2 text-sm font-medium ${
              activeTab === "all"
                ? "border-b-2 border-primary text-primary"
                : "text-gray-500"
            }`}
          >
            All Users ({filteredAllUsers.length})
          </button>
          <button
            onClick={() => setActiveTab("selected")}
            className={`flex-1 py-2 text-sm font-medium ${
              activeTab === "selected"
                ? "border-b-2 border-primary text-primary"
                : "text-gray-500"
            }`}
          >
            With Access ({accessUsers.length})
          </button>
        </div>

        {/* Scrollable Body */}
        <div>
          {activeTab === "all" && (
            <>
              <div className="p-4">
                {/* Search */}
                <input
                  type="text"
                  placeholder="Search users..."
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  className="w-full px-4 py-2 mb-3 border rounded text-sm"
                />
              </div>

              <div className="flex-1 overflow-y-auto h-[15rem]">
                {/* All Users List */}
                {filteredAllUsers.map((user) => (
                  <div
                    key={user.id}
                    className="flex items-center justify-between px-4 py-2 hover:bg-gray-50  border-t"
                  >
                    <span>
                      {user.first_name} {user.last_name}
                    </span>
                    {/* Toggle button */}
                    <button
                      onClick={() => toggleUser(user.id)}
                      className={`w-12 h-6 flex items-center rounded-full p-1 transition-colors ${
                        hasAccess(user.id)
                          ? "bg-primary justify-end"
                          : "bg-gray-300 justify-start"
                      }`}
                    >
                      <span className="w-4 h-4 bg-white rounded-full shadow-md"></span>
                    </button>
                  </div>
                ))}

                {filteredAllUsers.length === 0 && (
                  <p className="text-gray-500 text-sm text-center mt-4">
                    No users found
                  </p>
                )}
              </div>
            </>
          )}

          {activeTab === "selected" && (
            <>
              <div className="flex-1 overflow-y-auto h-[20.1rem]">
                {accessUsers.length > 0 ? (
                  accessUsers.map((user) => (
                    <div
                      key={user.id}
                      className="flex items-center justify-between px-4 py-2 hover:bg-gray-50 border-t last-of-type:border-b first-of-type:border-t-0"
                    >
                      <span>
                        {user.first_name} {user.last_name}
                      </span>
                      <button
                        onClick={() => toggleUser(user.id)}
                        className={`w-12 h-6 flex items-center rounded-full p-1 transition-colors ${
                          hasAccess(user.id)
                            ? "bg-primary justify-end"
                            : "bg-gray-300 justify-start"
                        }`}
                      >
                        <span className="w-4 h-4 bg-white rounded-full shadow-md"></span>
                      </button>
                    </div>
                  ))
                ) : (
                  <p className="text-gray-500 text-sm text-center mt-4">
                    No users have access yet.
                  </p>
                )}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-3 border-t bg-white sticky bottom-0 rounded-lg">
          <button
            onClick={() => onSave(selectedUsers)}
            disabled={!isChanged}
            className={`w-full py-2 rounded text-white transition-colors ${
              isChanged
                ? "bg-primary hover:bg-primary/80"
                : "bg-gray-400 cursor-not-allowed"
            }`}
          >
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
};

export default UserAccessModal;
